/**
 * Shapes aligned with Laravel API resources (PromptApiResource, SexPositionApiResource, fantasy catalog).
 */

import type { CoupleDynamicId, PlayerGender } from '../gameplay/coupleSetup';

export interface Tag {
  slug: string;
  name: string;
}

export type TranslationMap = Record<string, string>;

/** GET `/sex-toys` (resolved array, no Laravel wrapper). */
export interface SexToy {
  id: number;
  slug: string;
  audience: string;
  sort_order: number;
  title?: TranslationMap;
  description?: TranslationMap | null;
  primary_image_url: string | null;
  image_urls: string[] | null;
  has_images?: boolean;
  images_suppressed?: boolean;
  metadata?: {
    credit?: string | null;
  } | null;
}

export interface SexPosition {
  id: number;
  slug: string;
  sort_order: number;
  title?: TranslationMap;
  description?: TranslationMap;
  /** API uses `title` / `description`; some responses may expose raw columns */
  title_translations?: TranslationMap;
  description_translations?: TranslationMap;
  primary_image_url: string | null;
  image_urls: string[] | null;
  tags?: Tag[];
  metadata: Record<string, unknown> | null;
}

export type PromptType =
  'male_to_female' | 'female_to_male' | 'both' | 'general';

/** One answer card of a question card: a short text and an illustration recipe. */
export interface AnswerOption {
  /** Stable kebab-case key, sent back as `answer_key` on complete. */
  key: string;
  text: TranslationMap;
  /** Scene recipe for `renderScene` (`src/art/doodle/scene.mjs`); validated there. */
  art: unknown;
}

/** The answer chosen on the latest live turn (`GameSession.last_answer`). */
export interface LastAnswer {
  action_id: number;
  prompt_id: number;
  player: 'player1' | 'player2';
  /** The round the turn was played in. */
  round: number;
  key: string;
  text: TranslationMap;
  art: unknown;
}

export interface Prompt {
  id: number;
  category_id: number;
  sex_position_id?: number | null;
  type: PromptType | string;
  spice_level: number;
  explicit: boolean;
  is_active: boolean;
  text: string;
  text_translations?: TranslationMap;
  display_text?: string;
  display_text_translations?: TranslationMap;
  instruction_text?: string | null;
  instruction_text_translations?: TranslationMap | null;
  is_scratch_prompt?: boolean;
  image_urls: string[] | null;
  has_images?: boolean;
  images_suppressed?: boolean;
  tags?: Tag[];
  tags_player?: Tag[];
  category?: Category;
  sex_position?: SexPosition;
  sexPosition?: SexPosition;
  spice_level_requested?: number | null;
  spice_level_delivered?: number;
  filter_relaxed?: boolean;
  deck_repeat?: boolean;
  /**
   * Two-device `secret` card read by the player who is NOT on turn: the server
   * blanked `text` (and the translations, instructions, images and position)
   * and set this flag. Never true on the acting seat's own copy.
   */
  redacted?: boolean;
  /** Optional dare timer length (P1-BE3). Absent or null means no timer. */
  duration_seconds?: number | null;
  /** Question cards: 2 or 3 answer cards. Absent or null means a plain card. */
  answer_options?: AnswerOption[] | null;
  /**
   * Illustration recipe drawn from the card's own text (`renderScene`), shown
   * above the text. Absent or null: no picture. Nulled for a secret card.
   */
  art?: unknown | null;
  created_at?: string;
  updated_at?: string;
}

export interface Category {
  id: number;
  name: string;
  description: string;
  name_translations?: TranslationMap;
  description_translations?: TranslationMap;
  slug: string;
  is_active: boolean;
  parent_id: number | null;
  subCategories?: Category[];
}

export interface SetupSpiceLevelEntry {
  level: number;
  count: number;
}

export interface SpiceLevelAvailability {
  category_id: number;
  available_spice_levels: SetupSpiceLevelEntry[];
  min_level: number | null;
  max_level: number | null;
  total_prompts: number;
}

export interface CategoryAvailability {
  id: number;
  slug: string;
  parent_id: number | null;
  total_prompts: number;
  min_spice_level: number | null;
  max_spice_level: number | null;
  available_spice_levels: number[];
}

export type SetupAvailabilityParams = {
  game_mode?: 'solo' | 'couple';
  player1_gender?: 'male' | 'female';
  player2_gender?: 'male' | 'female';
  couple_dynamic?: string;
  /** Counts only the cards playable in this context (remote play drops in-person decks). */
  play_context?: PlayContext;
};

export interface GameSettings {
  id: number;
  user_id: number;
  language: string;
  show_explicit: boolean;
  difficulty_level: string;
  notifications_enabled: boolean;
  sound_enabled: boolean;
  /** When false, API omits prompt / sex-position image URLs. */
  show_prompt_images?: boolean;
  /** Word either player can say to stop (P1-BE1). Falls back to a translated "pause". */
  safe_word?: string | null;
  /** Server-side haptics flag (P1-BE1). Until it ships the app uses `hc_haptics` in localStorage. */
  haptics_enabled?: boolean;
  /** Couple defaults captured by onboarding (P1-BE1). Every key may be absent or null. */
  partner_name?: string | null;
  /** The player's own gender; set once and then locked by the API. */
  gender?: PlayerGender | null;
  couple_dynamic?: CoupleDynamicId | null;
  default_spice_min?: number | null;
  default_spice_max?: number | null;
  hidden_category_ids?: number[] | null;
  hidden_tag_slugs?: string[] | null;
  play_context?: PlayContext | null;
  /** Fantasy Match: my partner may see every answer of mine, `no` included. */
  share_all_fantasy_answers?: boolean;
}

export type PlayContext = 'in_person' | 'remote';

/** `two_devices`: each partner plays on their own phone (Phase 2). */
export type PlayMode = 'same_device' | 'two_devices';

/** The six emoji `POST /game/session/:id/react` accepts. */
export const SESSION_REACTIONS = ['❤️', '🔥', '😈', '😂', '😮', '👏'] as const;
export type SessionReaction = (typeof SESSION_REACTIONS)[number];

/** Body of POST /game/session. Mood sessions may omit category and spice values. */
export interface CreateGameSessionParams {
  game_mode: 'solo' | 'couple';
  player1_name: string;
  /** Optional on the server only when `settings.partner_name` is set. */
  player2_name?: string;
  category_id?: number;
  spice_level?: number;
  spice_level_min?: number;
  spice_level_max?: number;
  multiplayer_type?: 'same_account' | 'two_accounts';
  partner_name?: string;
  partner_email?: string;
  player1_gender?: PlayerGender;
  player2_gender?: PlayerGender;
  couple_dynamic?: string;
  mood_slug?: string;
  play_context?: PlayContext;
  /** `two_devices` needs an active couple (422 `couple_required` otherwise). */
  play_mode?: PlayMode;
  /**
   * Closes the partner's live two-device game instead of answering 422
   * `partner_session_active`. Only after an explicit confirmation.
   */
  replace_active?: boolean;
}

/** One-tap preset from GET /moods (P1-BE2). */
export interface Mood {
  id: number;
  slug: string;
  title: TranslationMap;
  subtitle: TranslationMap;
  category_slugs: string[];
  spice_min: number;
  spice_max: number;
  play_context: PlayContext | 'any';
  round_limit: number | null;
  image_url: string | null;
  card_count: number;
  /**
   * The server's verdict: enough cards for this user's settings to start a
   * session (`card_count` at or above its minimum). A mood that is not playable
   * is refused with 422 `mood_not_playable`.
   */
  playable: boolean;
}

/** `skipped` rows also carry `completed_at`: always read `status`. */
export type GameStatusState = 'completed' | 'skipped' | 'in_progress';

export interface GameStatus {
  id: number;
  user_id: number;
  prompt_id: number;
  progress: number;
  completed_at: string | null;
  /** Absent only on very old backends; see `isCompletedCard`. */
  status?: GameStatusState;
  created_at?: string;
  updated_at?: string;
  score: number;
  time_spent: number;
  prompt?: Prompt;
}

export interface RatingScaleEntry {
  value: number | string;
  label: TranslationMap;
}

/** One player's picks. `confirmed` is the two-device "I am done picking" flag. */
export interface FantasyExplorerSeatPicks {
  root_slugs: string[];
  leaf_by_root: Record<string, string | null>;
  confirmed?: boolean;
}

/** Persisted Fantasy Explorer wizard (stored on `game_sessions.flow_state`). */
export interface FantasyExplorerFlowState {
  type: 'fantasy_explorer';
  version?: number;
  top_n?: number;
  phase: string;
  player1?: FantasyExplorerSeatPicks;
  player2?: FantasyExplorerSeatPicks;
  ratings?: {
    player1?: Record<string, number>;
    player2?: Record<string, number>;
  };
  reveal_step?: number;
}

/** Top-level GET /fantasy-catalog */
export interface FantasyCatalogResponse {
  schema_version: number;
  rating_scale: {
    question?: TranslationMap;
    /** Keys are usually "1" … "5" */
    labels?: Record<string, TranslationMap>;
    min?: number;
    max?: number;
    step?: number;
    /** Legacy/alternate shapes */
    labelsArray?: RatingScaleEntry[];
  } | null;
  categories: FantasyCategory[];
}

export interface FantasyExplorerMatchPayload {
  strong: Array<{ root_slug: string; leaf_slug: string | null }>;
  soft: Array<{
    root_slug: string;
    leaf_1: string | null;
    leaf_2: string | null;
  }>;
  only_player1: string[];
  only_player2: string[];
}

export interface FantasyCategory {
  slug: string;
  letter: string | null;
  schema_version: number;
  sort_order: number;
  name: TranslationMap;
  description: TranslationMap;
  hero_image_url?: string | null;
  items: FantasyCatalogItem[];
}

export interface FantasyCatalogItem {
  id: number;
  slug: string;
  item_kind: string | null;
  sort_order: number;
  sex_position_id: number | null;
  title: TranslationMap;
  description: TranslationMap;
  question: TranslationMap;
  metadata: Record<string, unknown> | null;
  image_url?: string | null;
  sex_position?: SexPosition;
  variations?: FantasyCatalogItem[];
}

/** Laravel paginated JSON for GET /sex-positions */
export interface LaravelPaginatorMeta {
  current_page: number;
  from: number | null;
  last_page: number;
  per_page: number;
  to: number | null;
  total: number;
}

export interface PaginatedSexPositions {
  data: SexPosition[];
  meta: LaravelPaginatorMeta;
  links?: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
}

/** Laravel paginated JSON for GET /game/status */
export interface PaginatedGameStatus {
  data: GameStatus[];
  meta: LaravelPaginatorMeta;
  links?: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
}

export function pickTranslation(
  map: TranslationMap | undefined | null,
  language: string,
  fallback = 'en'
): string {
  if (!map || typeof map !== 'object') return '';
  const primary = map[language];
  if (primary) return primary;
  const fb = map[fallback];
  if (fb) return fb;
  const first = Object.values(map).find(v => v && String(v).trim());
  return first ? String(first) : '';
}
